import { useState, useEffect, useCallback, useRef } from 'react';
import { WSMessage } from '../types';

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageReceivedRef = useRef<((message: WSMessage) => void) | null>(null);
  const onDisconnectRef = useRef<(() => void) | null>(null);
  const messageQueueRef = useRef<WSMessage[]>([]);

  const connect = useCallback((serverURL: string) => {
    try {
      const ws = new WebSocket(serverURL);
      
      ws.onopen = () => {
        // console.log('WebSocket connected');
        setIsConnected(true);
        
        // Send queued messages
        while (messageQueueRef.current.length > 0) {
          const message = messageQueueRef.current.shift();
          if (message) {
            ws.send(JSON.stringify(message));
          }
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: WSMessage = JSON.parse(event.data);
          onMessageReceivedRef.current?.(message);
        } catch (error) {
          console.error('Failed to parse message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setLastError('Connection error');
      };

      ws.onclose = () => {
        // console.log('WebSocket disconnected');
        setIsConnected(false);
        onDisconnectRef.current?.();
      };

      wsRef.current = ws;
    } catch (error) {
      setLastError('Invalid URL');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setIsConnected(false);
      messageQueueRef.current = [];
      onDisconnectRef.current?.();
    }
  }, []);

  const send = useCallback((type: string, data?: any) => {
    const message: WSMessage = { type, data };
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      // Queue message if not connected yet
      // console.log('Queuing message:', type);
      messageQueueRef.current.push(message);
    }
  }, []);

  const setOnMessageReceived = useCallback((callback: (message: WSMessage) => void) => {
    onMessageReceivedRef.current = callback;
  }, []);

  const setOnDisconnect = useCallback((callback: () => void) => {
    onDisconnectRef.current = callback;
  }, []);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    lastError,
    connect,
    disconnect,
    send,
    setOnMessageReceived,
    setOnDisconnect
  };
};
